/**
 * AudioNotificationsPreview.jsx
 * ------------------------------
 * VISUAL MOCKUP PAGE for the upcoming "Audio Notifications"
 * (التنبيهات الصوتية) feature.
 *
 * NO LOGIC WIRED YET — this preview page renders the proposed designs
 * inside phone frames so the user can review the split-button layout,
 * the settings card, and the About-page copy before implementation.
 *
 * Route: /audio-preview
 */

import React, { useEffect, useState } from 'react';
import { useAstronomicalData } from '@/hooks/useAstronomicalData';
import { getCelestialBackground, getTextColors, getSurfaceColors } from '@/utils/celestialBackground';
import { Moon, Volume2, Play, Check, FileAudio, Info } from 'lucide-react';

const PHONE_W = 300;
const PHONE_H = 620;

/* ---------------------------------------------------------- Phone frame */
const PhoneFrame = ({ children, label, note }) => (
  <div className="flex flex-col items-center gap-3">
    <div
      className="rounded-[38px] p-2"
      style={{
        width: PHONE_W + 20,
        background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1119 100%)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      <div
        className="rounded-[30px] overflow-hidden relative"
        style={{ width: PHONE_W, height: PHONE_H }}
      >
        {/* Notch */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 z-30 rounded-full"
          style={{ width: 90, height: 20, background: '#0d1119' }}
        />
        {children}
      </div>
    </div>
    <div className="text-center max-w-[300px]">
      <div className="text-heading text-base text-white/90 mb-1">{label}</div>
      {note && <div className="text-body text-xs text-white/60 leading-relaxed">{note}</div>}
    </div>
  </div>
);

/* ---------------------------------------------------------- Shared row — the new split button */
const SplitButton = ({ textColors, surfaceColors, speakerActive = false }) => (
  <div className="flex items-stretch gap-2">
    {/* LONG part — keeps the current Tamam Al-Salah content */}
    <div
      className="flex-1 rounded-xl p-3 flex items-center gap-2.5"
      style={{
        background: `linear-gradient(135deg, ${textColors.accent}22, ${textColors.accent}11)`,
        border: `1.5px solid ${textColors.accent}66`,
        boxShadow: `0 0 20px ${textColors.accent}18`,
      }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: `${textColors.accent}33` }}
      >
        <Moon size={18} color={textColors.accent} />
      </div>
      <div className="flex-1 min-w-0" style={{ textAlign: 'right' }}>
        <div
          className="text-heading"
          style={{ color: textColors.primary, fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
        >
          تمام الصلاة
        </div>
        <div
          style={{ color: textColors.secondary, fontSize: 10, lineHeight: 1.3, marginTop: 2 }}
        >
          عدّاد الركعات مع لمسة واحدة
        </div>
      </div>
    </div>

    {/* SQUARE part — speaker icon only */}
    <button
      type="button"
      className="rounded-xl flex items-center justify-center flex-shrink-0 relative"
      style={{
        width: 56,
        height: 'auto',
        background: speakerActive
          ? `linear-gradient(135deg, ${textColors.accent}44, ${textColors.accent}22)`
          : 'rgba(255,255,255,0.06)',
        border: speakerActive
          ? `1.5px solid ${textColors.accent}aa`
          : `1.5px solid ${surfaceColors.border}`,
        boxShadow: speakerActive ? `0 0 20px ${textColors.accent}30` : 'none',
      }}
      aria-label="التنبيهات الصوتية"
    >
      <Volume2
        size={22}
        color={speakerActive ? textColors.accent : textColors.primary}
        strokeWidth={speakerActive ? 2.5 : 2}
      />
      {speakerActive && (
        <span
          className="absolute rounded-full"
          style={{
            top: 6,
            right: 6,
            width: 7,
            height: 7,
            background: '#10B981',
            boxShadow: '0 0 6px rgba(16,185,129,0.8)',
          }}
        />
      )}
    </button>
  </div>
);

/* ---------------------------------------------------------- Screen 1 — the new split button in context */
const Screen1_SplitButton = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex flex-col p-4 pt-12 overflow-hidden"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    {/* Mini prayer-times header (just for context) */}
    <div
      className="rounded-2xl p-3 mb-3"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="text-heading text-[11px] mb-2" style={{ color: textColors.primary }}>
        مواقيت الصلاة
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          ['الفجر', '٠٤:١٢'],
          ['الشروق', '٠٥:٣٢'],
          ['الظهر', '١٢:١٥'],
          ['العصر', '١٥:٤١'],
          ['المغرب', '١٨:٠٣'],
          ['العشاء', '١٩:٢٣'],
        ].map(([n, t], i) => (
          <div
            key={i}
            className="rounded-lg p-1.5 text-center"
            style={{
              background: i === 2 ? 'transparent' : 'rgba(255,255,255,0.05)',
              border: i === 2 ? `1px solid ${textColors.accent}` : '1px solid transparent',
            }}
          >
            <div style={{ color: textColors.secondary, fontSize: 8.5 }}>{n}</div>
            <div style={{ color: textColors.primary, fontSize: 11, fontWeight: 500 }}>{t}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Spacing */}
    <div style={{ height: 10 }} />

    {/* The NEW split button */}
    <SplitButton textColors={textColors} surfaceColors={surfaceColors} speakerActive={false} />

    {/* Caption */}
    <div
      className="mt-4 rounded-lg p-2.5 flex items-start gap-2"
      style={{
        background: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.25)',
      }}
    >
      <Info size={12} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
      <div style={{ color: textColors.primary, fontSize: 10, lineHeight: 1.4 }}>
        زر جديد ⟵ أيقونة مكبّر الصوت (التنبيهات الصوتية)
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 2 — Audio Notifications card (empty state) */
const Screen2_CardEmpty = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex items-center justify-center p-3"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    <div
      className="rounded-2xl p-4 w-full"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Title + close */}
      <div className="flex items-center gap-2 mb-3">
        <Volume2 size={20} color={textColors.accent} />
        <div className="text-heading flex-1" style={{ color: textColors.primary, fontSize: 15 }}>
          التنبيهات الصوتية
        </div>
        <div
          className="rounded-full flex items-center justify-center cursor-pointer"
          style={{
            width: 24,
            height: 24,
            background: 'rgba(255,255,255,0.08)',
            color: textColors.primary,
            fontSize: 14,
          }}
        >
          ×
        </div>
      </div>

      {/* Honesty banner */}
      <div
        className="rounded-lg p-2 mb-3 flex items-start gap-2"
        style={{
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
        }}
      >
        <Info size={11} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
        <div style={{ color: textColors.primary, fontSize: 9.5, lineHeight: 1.4 }}>
          تعمل حين يكون التطبيق مفتوحاً — إشعارات الخلفية في مرحلة قادمة
        </div>
      </div>

      {/* Volume slider */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div style={{ color: textColors.secondary, fontSize: 10 }}>مستوى الصوت</div>
          <div style={{ color: textColors.accent, fontSize: 10, fontWeight: 600 }}>٦٥٪</div>
        </div>
        <div
          className="relative h-1.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{
              width: '65%',
              background: `linear-gradient(90deg, ${textColors.accent}aa, ${textColors.accent})`,
            }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 rounded-full"
            style={{
              right: '35%',
              width: 14,
              height: 14,
              background: textColors.accent,
              boxShadow: `0 0 10px ${textColors.accent}80`,
            }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span style={{ color: textColors.secondary, fontSize: 8.5 }}>إغلاق</span>
          <span style={{ color: textColors.secondary, fontSize: 8.5 }}>عالٍ</span>
        </div>
        <div
          className="mt-1.5 text-center"
          style={{ color: textColors.secondary, fontSize: 8.5, fontStyle: 'italic' }}
        >
          تأكّد من رفع صوت الهاتف أيضاً
        </div>
      </div>

      {/* File picker */}
      <div className="mb-3">
        <div style={{ color: textColors.secondary, fontSize: 10, marginBottom: 6 }}>
          النغمة
        </div>
        <button
          type="button"
          className="w-full rounded-lg p-2.5 flex items-center gap-2 border-dashed"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: `1.5px dashed ${surfaceColors.border}`,
          }}
        >
          <FileAudio size={16} color={textColors.secondary} />
          <span style={{ color: textColors.secondary, fontSize: 10.5, flex: 1, textAlign: 'right' }}>
            اختر نغمة من هاتفك
          </span>
          <span
            className="rounded-md px-2 py-0.5"
            style={{
              background: `${textColors.accent}22`,
              color: textColors.accent,
              fontSize: 9,
              fontWeight: 600,
            }}
          >
            تصفّح
          </span>
        </button>
        <div style={{ color: textColors.secondary, fontSize: 8.5, marginTop: 4, textAlign: 'center' }}>
          النغمة محفوظة محلياً على جهازك • الحد الأقصى: ٥ ميجابايت
        </div>
      </div>

      {/* Repeat count */}
      <div className="mb-3">
        <div style={{ color: textColors.secondary, fontSize: 10, marginBottom: 6 }}>
          عدد مرات التكرار
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { k: '1', label: 'مرة' },
            { k: '2', label: 'مرتين', active: true },
            { k: '3', label: 'ثلاثاً' },
          ].map((opt) => (
            <div
              key={opt.k}
              className="rounded-md py-1.5 text-center"
              style={{
                background: opt.active ? `${textColors.accent}22` : 'transparent',
                border: opt.active
                  ? `1.5px solid ${textColors.accent}aa`
                  : `1px solid ${surfaceColors.border}`,
                color: textColors.primary,
                fontSize: 10,
                fontWeight: opt.active ? 600 : 400,
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${surfaceColors.border}`,
            color: textColors.secondary,
            fontSize: 10.5,
          }}
        >
          <Play size={12} />
          تجربة
        </button>
        <button
          type="button"
          className="flex-1 py-2 rounded-lg text-heading"
          style={{
            background: `linear-gradient(135deg, ${textColors.accent}, ${textColors.accent}dd)`,
            color: '#0d1119',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          حفظ
        </button>
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 3 — Card after user picked a file */
const Screen3_CardSelected = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex items-center justify-center p-3"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    <div
      className="rounded-2xl p-4 w-full"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Volume2 size={20} color={textColors.accent} />
        <div className="text-heading flex-1" style={{ color: textColors.primary, fontSize: 15 }}>
          التنبيهات الصوتية
        </div>
        <div
          className="rounded-full flex items-center justify-center cursor-pointer"
          style={{
            width: 24,
            height: 24,
            background: 'rgba(255,255,255,0.08)',
            color: textColors.primary,
            fontSize: 14,
          }}
        >
          ×
        </div>
      </div>

      {/* Honesty banner */}
      <div
        className="rounded-lg p-2 mb-3 flex items-start gap-2"
        style={{
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
        }}
      >
        <Info size={11} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
        <div style={{ color: textColors.primary, fontSize: 9.5, lineHeight: 1.4 }}>
          تعمل حين يكون التطبيق مفتوحاً — إشعارات الخلفية في مرحلة قادمة
        </div>
      </div>

      {/* Volume at higher level */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div style={{ color: textColors.secondary, fontSize: 10 }}>مستوى الصوت</div>
          <div style={{ color: textColors.accent, fontSize: 10, fontWeight: 600 }}>٨٠٪</div>
        </div>
        <div
          className="relative h-1.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{
              width: '80%',
              background: `linear-gradient(90deg, ${textColors.accent}aa, ${textColors.accent})`,
            }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 rounded-full"
            style={{
              right: '20%',
              width: 14,
              height: 14,
              background: textColors.accent,
              boxShadow: `0 0 10px ${textColors.accent}80`,
            }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span style={{ color: textColors.secondary, fontSize: 8.5 }}>إغلاق</span>
          <span style={{ color: textColors.secondary, fontSize: 8.5 }}>عالٍ</span>
        </div>
      </div>

      {/* File picker — selected state */}
      <div className="mb-3">
        <div style={{ color: textColors.secondary, fontSize: 10, marginBottom: 6 }}>
          النغمة المختارة
        </div>
        <div
          className="w-full rounded-lg p-2.5 flex items-center gap-2"
          style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1.5px solid rgba(16, 185, 129, 0.5)',
          }}
        >
          <Check size={14} color="#10B981" />
          <span
            style={{
              color: textColors.primary,
              fontSize: 10.5,
              flex: 1,
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            notification.mp3
          </span>
          <span style={{ color: textColors.secondary, fontSize: 9 }}>١٫٢ م.ب</span>
          <span
            className="rounded-md px-2 py-0.5 cursor-pointer"
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: textColors.secondary,
              fontSize: 9,
            }}
          >
            تغيير
          </span>
        </div>
      </div>

      {/* Repeat count */}
      <div className="mb-3">
        <div style={{ color: textColors.secondary, fontSize: 10, marginBottom: 6 }}>
          عدد مرات التكرار
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { k: '1', label: 'مرة' },
            { k: '2', label: 'مرتين', active: true },
            { k: '3', label: 'ثلاثاً' },
          ].map((opt) => (
            <div
              key={opt.k}
              className="rounded-md py-1.5 text-center"
              style={{
                background: opt.active ? `${textColors.accent}22` : 'transparent',
                border: opt.active
                  ? `1.5px solid ${textColors.accent}aa`
                  : `1px solid ${surfaceColors.border}`,
                color: textColors.primary,
                fontSize: 10,
                fontWeight: opt.active ? 600 : 400,
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${surfaceColors.border}`,
            color: textColors.primary,
            fontSize: 10.5,
          }}
        >
          <Play size={12} />
          تجربة
        </button>
        <button
          type="button"
          className="flex-1 py-2 rounded-lg text-heading"
          style={{
            background: `linear-gradient(135deg, ${textColors.accent}, ${textColors.accent}dd)`,
            color: '#0d1119',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          حفظ
        </button>
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 4 — Button after save (active state) */
const Screen4_ActiveState = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex flex-col p-4 pt-12 overflow-hidden"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    {/* Mini prayer-times header */}
    <div
      className="rounded-2xl p-3 mb-3"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="text-heading text-[11px] mb-2" style={{ color: textColors.primary }}>
        مواقيت الصلاة
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          ['الفجر', '٠٤:١٢'],
          ['الشروق', '٠٥:٣٢'],
          ['الظهر', '١٢:١٥'],
          ['العصر', '١٥:٤١'],
          ['المغرب', '١٨:٠٣'],
          ['العشاء', '١٩:٢٣'],
        ].map(([n, t], i) => (
          <div
            key={i}
            className="rounded-lg p-1.5 text-center"
            style={{
              background: i === 2 ? 'transparent' : 'rgba(255,255,255,0.05)',
              border: i === 2 ? `1px solid ${textColors.accent}` : '1px solid transparent',
            }}
          >
            <div style={{ color: textColors.secondary, fontSize: 8.5 }}>{n}</div>
            <div style={{ color: textColors.primary, fontSize: 11, fontWeight: 500 }}>{t}</div>
          </div>
        ))}
      </div>
    </div>

    <div style={{ height: 10 }} />

    {/* Split button — SPEAKER ACTIVE (green dot) */}
    <SplitButton textColors={textColors} surfaceColors={surfaceColors} speakerActive={true} />

    <div
      className="mt-4 rounded-lg p-2.5 flex items-start gap-2"
      style={{
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
      }}
    >
      <Check size={12} color="#10B981" className="mt-0.5 flex-shrink-0" />
      <div style={{ color: textColors.primary, fontSize: 10, lineHeight: 1.4 }}>
        التنبيهات مُفعّلة • النقطة الخضراء تؤكّد أن النغمة محفوظة
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 5 — About page addition */
const Screen5_AboutExcerpt = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex items-start justify-center p-3 pt-12 overflow-hidden"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    <div
      className="rounded-2xl p-4 w-full"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="text-heading mb-2" style={{ color: textColors.primary, fontSize: 13 }}>
        خدمات التطبيق
      </div>

      {/* Previous item */}
      <div className="mb-3 pb-3 border-b" style={{ borderColor: surfaceColors.border }}>
        <div className="flex items-center gap-1.5 mb-1">
          <Moon size={11} color={textColors.accent} />
          <div style={{ color: textColors.primary, fontSize: 11, fontWeight: 600 }}>
            تمام الصلاة
          </div>
        </div>
        <div style={{ color: textColors.secondary, fontSize: 9.5, lineHeight: 1.5 }}>
          عدّاد الركعات مع لمسة واحدة في بيئة بصريّة هادئة...
        </div>
      </div>

      {/* NEW item — Audio notifications */}
      <div
        className="rounded-lg p-2.5"
        style={{
          background: `${textColors.accent}0a`,
          border: `1px solid ${textColors.accent}44`,
        }}
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <Volume2 size={11} color={textColors.accent} />
          <div style={{ color: textColors.primary, fontSize: 11, fontWeight: 600 }}>
            التنبيهات الصوتية
          </div>
          <span
            className="rounded-full px-1.5 py-0.5"
            style={{
              background: '#DC2626',
              color: '#fff',
              fontSize: 7.5,
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}
          >
            جديد
          </span>
        </div>
        <div style={{ color: textColors.secondary, fontSize: 9.5, lineHeight: 1.6 }}>
          أضف لمسة شخصية لتذكير نفسك بأوقات الصلاة عبر نغمة صوتية تختارها من هاتفك،
          مع التحكّم بمستوى الصوت وعدد مرات التكرار.
        </div>
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Main page */
export default function AudioNotificationsPreview() {
  const [useGPS] = useState(true);
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
  }, []);

  const { astronomicalData } = useAstronomicalData(null, useGPS);
  const sunPosition = astronomicalData?.sun_position;
  const celestialStyle = getCelestialBackground(sunPosition);
  const textColors = getTextColors(sunPosition?.phase || 'night');
  const surfaceColors = getSurfaceColors(sunPosition?.phase || 'night');

  const screens = [
    {
      key: 's1',
      label: '١ — الزرّ المُقسَّم (قبل التفعيل)',
      note: 'الجزء الطويل يحتفظ بكل محتوى تمام الصلاة • المربّع الصغير يحوي أيقونة مكبّر الصوت فقط',
      render: <Screen1_SplitButton celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
    {
      key: 's2',
      label: '٢ — بطاقة التنبيهات (أول فتح)',
      note: 'سلايد الصوت + زر اختيار النغمة + خيار التكرار + لافتة شفافية "تعمل عند فتح التطبيق"',
      render: <Screen2_CardEmpty celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
    {
      key: 's3',
      label: '٣ — بعد اختيار النغمة',
      note: 'شريط أخضر يُظهر الملف المختار + حجمه + زر "تغيير" سريع',
      render: <Screen3_CardSelected celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
    {
      key: 's4',
      label: '٤ — الزرّ بعد التفعيل',
      note: 'المربّع الصغير يُضيء بالذهبي + نقطة خضراء صغيرة تؤكّد وجود نغمة محفوظة',
      render: <Screen4_ActiveState celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
    {
      key: 's5',
      label: '٥ — إضافة في صفحة "من نحن"',
      note: 'عنصر جديد تحت تمام الصلاة بشارة "جديد" حمراء — موجز في سطرين',
      render: <Screen5_AboutExcerpt celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
  ];

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{ background: celestialStyle.background }}
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs mb-4"
            style={{
              background: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              color: '#F59E0B',
            }}
            data-testid="preview-badge"
          >
            معاينة تصميم — قبل التنفيذ
          </div>
          <h1 className="text-heading text-4xl md:text-5xl mb-3" style={{ color: textColors.primary }}>
            التنبيهات الصوتية
          </h1>
          <p className="text-body text-sm md:text-base max-w-xl mx-auto" style={{ color: textColors.secondary }}>
            خمس شاشات تُظهر الزرّ المُقسَّم، بطاقة الإعدادات، والإضافة في صفحة من نحن —
            كلّها بأسلوب فلك البصري وبخلفيّته الديناميّة
          </p>
        </div>

        {/* Screens grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 justify-items-center">
          {screens.map((s) => (
            <PhoneFrame key={s.key} label={s.label} note={s.note}>
              {s.render}
            </PhoneFrame>
          ))}
        </div>

        {/* Spec table */}
        <div
          className="mt-16 rounded-2xl p-6 max-w-3xl mx-auto"
          style={{
            background: surfaceColors.card,
            border: `1px solid ${surfaceColors.border}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <h2 className="text-heading text-xl mb-4" style={{ color: textColors.primary }}>
            المواصفات المُقرّة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" style={{ color: textColors.primary }}>
            {[
              ['الاسم', 'التنبيهات الصوتية'],
              ['موقع الزرّ', 'جزء مُقتطع من زرّ تمام الصلاة (مربّع ٥٦ بكسل)'],
              ['أيقونة الزرّ', 'مكبّر صوت (Volume2)'],
              ['مؤشّر التفعيل', 'نقطة خضراء صغيرة في الزاوية'],
              ['عناصر البطاقة', 'سلايد الصوت • اختيار نغمة • عدد التكرار • زر تجربة • زر حفظ'],
              ['مدى الصوت', 'من ٠٪ (إغلاق) إلى ١٠٠٪ (عالٍ)'],
              ['تنويه الصوت', '"تأكّد من رفع صوت الهاتف أيضاً"'],
              ['أنواع الملفات', 'MP3 • WAV • OGG'],
              ['الحجم الأقصى', '٥ ميجابايت'],
              ['تخزين النغمة', 'IndexedDB — محلياً على الجهاز'],
              ['خيارات التكرار', 'مرة • مرتين (افتراضي) • ثلاث مرات'],
              ['زر التجربة', 'يُشغّل النغمة فوراً للمعاينة'],
              ['لافتة الشفافية', '"تعمل عند فتح التطبيق — إشعارات الخلفية لاحقاً"'],
              ['الأذانات المدمجة', 'أُلغيت في المرحلة الحالية ✓'],
              ['اللغات', '١٣ لغة'],
              ['صفحة من نحن', 'عنصر جديد مع شارة "جديد" حمراء'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b gap-3" style={{ borderColor: surfaceColors.border }}>
                <span style={{ color: textColors.secondary }}>{k}</span>
                <span className="text-heading text-xs md:text-sm text-left" style={{ textAlign: 'left' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-10 text-center">
          <a
            href="/"
            className="inline-block text-sm underline"
            style={{ color: textColors.accent }}
            data-testid="back-to-app-link"
          >
            ← العودة إلى فلك
          </a>
        </div>
      </div>
    </div>
  );
}
